import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { HeroPhoneMockup } from "@/components/landing/HeroPhoneMockup";
import {
  InstagramBrandTile,
  WhatsAppBrandTile,
} from "@/components/landing/shared/ChannelBrandIcons";
import { cn } from "@/lib/utils";

const ROTATING_WORDS = ["vendedor", "atendente", "SDR"] as const;
const WORD_ROTATE_MS = 3400;
const HERO_EASE = "cubic-bezier(0.22, 1, 0.36, 1)";

function prefersReducedMotion() {
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

function TypingWord({ word }: { word: string }) {
  const [displayed, setDisplayed] = useState(word);
  const [cursorVisible, setCursorVisible] = useState(true);

  useEffect(() => {
    if (prefersReducedMotion()) {
      setDisplayed(word);
      return;
    }

    let i = 0;
    setDisplayed("");
    const interval = window.setInterval(() => {
      i += 1;
      setDisplayed(word.slice(0, i));
      if (i >= word.length) window.clearInterval(interval);
    }, 62);

    return () => window.clearInterval(interval);
  }, [word]);

  useEffect(() => {
    if (prefersReducedMotion()) return;
    const blink = window.setInterval(() => {
      setCursorVisible((visible) => !visible);
    }, 560);
    return () => window.clearInterval(blink);
  }, []);

  return (
    <span className="inline-flex items-baseline">
      <span className="text-cyan-200">{displayed}</span>
      <span
        className={cn(
          "ml-[0.15ch] text-cyan-200/80 motion-reduce:hidden",
          cursorVisible ? "opacity-100" : "opacity-0",
          "motion-safe:transition-opacity motion-safe:duration-75",
        )}
        aria-hidden
      >
        |
      </span>
    </span>
  );
}

function heroReveal(ready: boolean, delayMs: number) {
  return cn(
    "motion-safe:transition-[opacity,transform] motion-safe:duration-[750ms]",
    "motion-reduce:translate-y-0 motion-reduce:opacity-100",
    ready ? "translate-y-0 opacity-100" : "translate-y-3.5 opacity-0",
  );
}

export const Hero = () => {
  const [wordIdx, setWordIdx] = useState(0);
  const [stripX, setStripX] = useState("-50%");
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (prefersReducedMotion()) {
      setReady(true);
      return;
    }
    const frame = window.requestAnimationFrame(() => setReady(true));
    return () => window.cancelAnimationFrame(frame);
  }, []);

  useEffect(() => {
    const rotate = window.setInterval(() => {
      setWordIdx((i) => (i + 1) % ROTATING_WORDS.length);
    }, WORD_ROTATE_MS);
    return () => window.clearInterval(rotate);
  }, []);

  const handleMouseMove = (e: React.MouseEvent) => {
    if (prefersReducedMotion()) return;
    const center = window.innerWidth / 2;
    const distance = (e.clientX - center) / center;
    const maxOffset = 52;
    setStripX(`calc(-50% + ${distance * maxOffset}px)`);
  };

  const handleMouseLeave = () => {
    setStripX("-50%");
  };

  return (
    <section
      className="relative flex min-h-[100vh] flex-col justify-center overflow-hidden pt-28 pb-24 md:pt-32 md:pb-32"
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
    >
      <div
        className="absolute inset-0 bg-[radial-gradient(ellipse_120%_80%_at_50%_-20%,hsl(270_65%_28%)_0%,hsl(262_55%_14%)_45%,hsl(240_35%_8%)_100%)]"
        aria-hidden
      />
      <div
        className="pointer-events-none absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-black/20"
        aria-hidden
      />

      <div
        className="absolute bottom-0 left-1/2 z-20 motion-safe:transition-transform motion-safe:duration-500 motion-safe:ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none"
        style={{ transform: `translateX(${stripX})` }}
      >
        <div
          className={cn(
            "w-[95vw] max-w-none rounded-t-2xl border border-b-0 border-white/12 bg-white/[0.06] px-5 py-1.5 backdrop-blur-md md:w-auto md:rounded-t-[24px] md:px-10 md:py-2",
            heroReveal(ready, 520),
          )}
          style={{ transitionTimingFunction: HERO_EASE, transitionDelay: "520ms" }}
        >
          <p className="text-center text-xs font-medium tracking-wide text-white/80 md:text-sm">
            Atendimento que não para quando a equipe sai.
          </p>
        </div>
      </div>

      <div className="container relative z-10 mx-auto px-4">
        <div className="mx-auto grid max-w-6xl items-center gap-12 lg:grid-cols-2 lg:gap-10">
          <div className="space-y-6 text-center lg:pr-4 lg:text-left">
            <div
              className={cn(
                "flex flex-wrap items-center justify-center gap-3 sm:gap-4 lg:justify-start",
                heroReveal(ready, 0),
              )}
              style={{ transitionTimingFunction: HERO_EASE }}
            >
              <img
                src="/logo.png?v=6"
                alt="FabChat"
                className="hidden h-7 w-auto shrink-0 object-contain md:block"
              />
              <div
                className="flex items-center gap-2.5"
                aria-label="WhatsApp, Instagram e CRM integrado"
              >
                <WhatsAppBrandTile className="h-8 w-8 rounded-lg" iconClassName="h-4 w-4" />
                <InstagramBrandTile className="h-8 w-8 rounded-lg" iconClassName="h-4 w-4" />
                <span className="text-sm font-medium text-white/75">CRM integrado</span>
              </div>
            </div>

            <h1
              className={cn(
                "text-4xl font-semibold leading-[1.15] tracking-tight text-white sm:text-5xl lg:text-[2.65rem] xl:text-6xl",
                heroReveal(ready, 100),
              )}
              style={{ transitionTimingFunction: HERO_EASE, transitionDelay: "100ms" }}
            >
              <span className="sr-only">FabChat — agentes de IA para WhatsApp e Instagram com CRM. </span>
              <span className="block text-balance">Seu melhor</span>
              <span className="mt-2 flex flex-wrap items-center justify-center lg:justify-start">
                <span
                  className="inline-flex min-w-[9ch] items-baseline text-left font-semibold sm:min-w-[10ch]"
                  aria-live="polite"
                  aria-atomic="true"
                >
                  <TypingWord word={ROTATING_WORDS[wordIdx]} />
                </span>
                <span className="ml-2">com IA</span>
              </span>
            </h1>

            <p
              className={cn(
                "mx-auto max-w-xl text-lg font-normal leading-relaxed text-violet-100/90 sm:text-xl lg:mx-0",
                heroReveal(ready, 200),
              )}
              style={{ transitionTimingFunction: HERO_EASE, transitionDelay: "200ms" }}
            >
              Agentes para WhatsApp e Instagram com CRM integrado. Qualifique leads, confirme horários e
              mantenha o contexto. Sua equipe entra quando precisa de humano.
            </p>

            <div
              className={cn("flex justify-center pt-2 lg:justify-start", heroReveal(ready, 320))}
              style={{ transitionTimingFunction: HERO_EASE, transitionDelay: "320ms" }}
            >
              <Button
                size="lg"
                className="rounded-full bg-primary px-8 py-6 text-base font-medium text-primary-foreground shadow-md motion-safe:transition-transform motion-safe:duration-200 motion-safe:ease-[cubic-bezier(0.22,1,0.36,1)] motion-safe:hover:scale-[1.02] hover:bg-primary/90"
                asChild
              >
                <a href="#como-funciona">Ver como funciona</a>
              </Button>
            </div>
          </div>

          <div
            className={cn("hidden justify-center md:flex lg:justify-end", heroReveal(ready, 420))}
            style={{ transitionTimingFunction: HERO_EASE, transitionDelay: "420ms" }}
          >
            <HeroPhoneMockup />
          </div>
        </div>

        <div
          className={cn(
            "mx-auto mt-14 flex max-w-sm justify-center md:hidden",
            heroReveal(ready, 480),
          )}
          style={{ transitionTimingFunction: HERO_EASE, transitionDelay: "480ms" }}
        >
          <HeroPhoneMockup />
        </div>
      </div>
    </section>
  );
};
